<template>
  <section class="tc-card" data-testid="traffic-card">
    <div class="tc-head">
      <div>
        <div class="tc-label">Site traffic · Cloudflare</div>
        <p class="tc-sub">Pageviews & visitors for <span class="tc-mono">{{ domainLabel }}</span> — last {{ days }} days.</p>
      </div>
      <div class="tc-range">
        <button v-for="d in [7, 30, 90]" :key="d" class="tc-range-btn" :class="{ active: days === d }" @click="setDays(d)">{{ d }}d</button>
      </div>
    </div>

    <p v-if="loading" class="tc-muted">Loading traffic…</p>

    <template v-else-if="traffic && traffic.status === 'ok'">
      <div class="tc-stats">
        <div class="tc-stat"><span class="tc-val">{{ fmt(traffic.totals.pageViews) }}</span><span class="tc-stat-label">Pageviews</span></div>
        <div class="tc-stat"><span class="tc-val">{{ fmt(traffic.totals.uniques) }}</span><span class="tc-stat-label">Unique visitors</span></div>
        <div class="tc-stat"><span class="tc-val">{{ fmt(traffic.totals.requests) }}</span><span class="tc-stat-label">Requests</span></div>
      </div>
      <svg v-if="spark.points" class="tc-spark" viewBox="0 0 300 60" preserveAspectRatio="none" role="img" aria-label="Daily pageviews">
        <polyline :points="spark.area" class="tc-spark-fill" />
        <polyline :points="spark.points" class="tc-spark-line" />
        <circle :cx="spark.lastX" :cy="spark.lastY" r="3" class="tc-spark-dot" />
      </svg>
      <p class="tc-foot">Peak day {{ fmt(spark.peak) }} pageviews · checked {{ shortTime(traffic.checked_at) }}</p>
    </template>

    <div v-else class="tc-empty">
      <p class="tc-empty-title">{{ emptyTitle }}</p>
      <p class="tc-muted">{{ traffic?.reason || 'Traffic is unavailable right now.' }}</p>
      <p v-if="traffic?.status === 'unauthorized'" class="tc-hint">
        Add <span class="tc-mono">Analytics : Read</span> to the Cloudflare API token (or set <span class="tc-mono">CF_ANALYTICS_API_TOKEN</span>) to light this up — no site changes needed.
      </p>
    </div>

    <!-- Microsoft Clarity — behaviour, alongside Cloudflare traffic -->
    <div class="tc-clarity">
      <div class="tc-clarity-label">Behaviour · Microsoft Clarity</div>
      <p v-if="clarityLoading" class="tc-muted">Loading Clarity…</p>
      <template v-else-if="clarity && clarity.status === 'ok'">
        <div class="tc-stats">
          <div class="tc-stat"><span class="tc-val">{{ fmt(m.sessions) }}</span><span class="tc-stat-label">Sessions</span></div>
          <div class="tc-stat"><span class="tc-val">{{ fmt(m.distinct_users) }}</span><span class="tc-stat-label">Users</span></div>
          <div class="tc-stat"><span class="tc-val">{{ fmt(m.pages_per_session, 2) }}</span><span class="tc-stat-label">Pages / session</span></div>
        </div>
        <div class="tc-behaviour">
          <span class="tc-beh"><b>{{ fmt(m.rage_clicks) }}</b> rage clicks</span>
          <span class="tc-beh"><b>{{ fmt(m.dead_clicks) }}</b> dead clicks</span>
          <span class="tc-beh"><b>{{ fmt(m.quickback_clicks) }}</b> quick-backs</span>
          <span class="tc-beh"><b>{{ fmt(m.script_errors) }}</b> script errors</span>
        </div>
        <p class="tc-foot">Last {{ clarityDays }} day{{ clarityDays > 1 ? 's' : '' }}{{ days > 3 ? ' · Clarity max is 3d' : '' }} · {{ clarity.cached ? 'cached' : 'live' }}{{ clarity.stale ? ' (stale)' : '' }}</p>
      </template>
      <div v-else class="tc-clarity-empty">
        <p class="tc-muted">{{ clarity?.reason || 'Clarity behaviour not available.' }}</p>
        <p v-if="clarity?.status === 'not_configured'" class="tc-hint">Enable Microsoft Clarity for this site to capture behaviour.</p>
        <p v-else-if="clarity?.status === 'no_token'" class="tc-hint">Add a <span class="tc-mono">Clarity Data Export API token</span> (Clarity → Settings → Data Export) to pull sessions, rage/dead clicks and scroll depth here.</p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '../utils/api'

const props = defineProps({ siteId: { type: String, required: true } })

const traffic = ref(null)
const loading = ref(false)
const days = ref(7)
const clarity = ref(null)
const clarityLoading = ref(false)
const m = computed(() => clarity.value?.metrics || {})

const domainLabel = computed(() => traffic.value?.domain || 'this site')
const emptyTitle = computed(() => ({
  unauthorized: 'Traffic not connected yet',
  no_zone: 'No Cloudflare zone for this site',
  unavailable: 'Traffic unavailable',
}[traffic.value?.status] || 'No traffic data'))

const fmt = (n, dp) => (n === null || n === undefined ? '—' : (dp ? Number(n).toFixed(dp) : Number(n).toLocaleString()))
const shortTime = (v) => (v ? new Date(v).toLocaleString([], { hour: '2-digit', minute: '2-digit' }) : '—')

// Inline sparkline of daily pageviews, scaled into the 300×60 viewBox.
const spark = computed(() => {
  const s = traffic.value?.series || []
  if (s.length < 2) return { points: null }
  const vals = s.map((d) => d.pageViews)
  const peak = Math.max(...vals, 1)
  const stepX = 300 / (s.length - 1)
  const y = (v) => 58 - (v / peak) * 52
  const pts = s.map((d, i) => `${(i * stepX).toFixed(1)},${y(d.pageViews).toFixed(1)}`)
  const lastX = ((s.length - 1) * stepX).toFixed(1)
  const lastY = y(vals[vals.length - 1]).toFixed(1)
  return {
    points: pts.join(' '),
    area: `0,60 ${pts.join(' ')} 300,60`,
    lastX, lastY, peak,
  }
})

async function load() {
  loading.value = true
  try {
    const { data } = await api.get(`/sites/${props.siteId}/traffic`, { params: { days: days.value } })
    traffic.value = data.traffic
  } catch (e) {
    traffic.value = { status: 'unavailable', reason: e?.response?.data?.error || 'Failed to load traffic', series: [] }
  } finally {
    loading.value = false
  }
}
function setDays(d) { if (d !== days.value) { days.value = d; load(); loadClarity() } }

// Clarity's Data Export API only supports 1-3 days, so the shared day filter
// clamps here to its 3-day max (7/30/90 all read the last 3 days).
const clarityDays = computed(() => Math.min(days.value, 3))

async function loadClarity() {
  clarityLoading.value = true
  try {
    const { data } = await api.get(`/sites/${props.siteId}/clarity`, { params: { numOfDays: clarityDays.value } })
    clarity.value = data.clarity
  } catch (e) {
    clarity.value = { status: 'unavailable', reason: e?.response?.data?.error || 'Failed to load Clarity' }
  } finally {
    clarityLoading.value = false
  }
}

onMounted(() => { load(); loadClarity() })
</script>

<style scoped>
.tc-card {
  background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-md);
  padding: 16px 18px; color: var(--text-primary);
  backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur); box-shadow: var(--elev-sm);
}
.tc-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.tc-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--accent-light); }
.tc-sub { font-size: 0.82rem; color: var(--text-secondary); margin: 4px 0 0; }
.tc-mono { font-family: ui-monospace, SFMono-Regular, monospace; color: var(--text-primary); }
.tc-range { display: inline-flex; background: var(--glass-bg); border: var(--glass-border); border-radius: var(--radius-sm); padding: 2px; }
.tc-range-btn { padding: 4px 10px; border-radius: 6px; background: transparent; border: none; color: var(--text-secondary); font-size: 0.75rem; font-weight: 600; cursor: pointer; }
.tc-range-btn.active { background: var(--glass-bg-active); color: var(--text-primary); }

.tc-muted { color: var(--text-secondary); font-size: 0.85rem; margin: 4px 0; }
.tc-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 12px; }
.tc-stat { display: flex; flex-direction: column; gap: 3px; }
.tc-val { font-size: 1.5rem; font-weight: 700; font-variant-numeric: tabular-nums; line-height: 1.1; }
.tc-stat-label { font-size: 0.72rem; color: var(--text-secondary); }

.tc-spark { width: 100%; height: 60px; display: block; }
.tc-spark-line { fill: none; stroke: var(--accent); stroke-width: 2; vector-effect: non-scaling-stroke; }
.tc-spark-fill { fill: var(--accent-soft); stroke: none; }
.tc-spark-dot { fill: var(--accent-light); }
.tc-foot { font-size: 0.72rem; color: var(--text-muted); margin: 8px 0 0; }

.tc-clarity { margin-top: 16px; padding-top: 14px; border-top: var(--glass-border); }
.tc-clarity-label { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.08em; color: #d4a5f5; margin-bottom: 10px; }
.tc-behaviour { display: flex; gap: 16px; flex-wrap: wrap; margin-top: 4px; font-size: 0.82rem; color: var(--text-secondary); }
.tc-beh b { color: var(--text-primary); font-variant-numeric: tabular-nums; }
.tc-clarity-empty { padding: 2px 0; }

.tc-empty { padding: 8px 0; }
.tc-empty-title { font-weight: 600; color: var(--text-primary); margin: 0 0 4px; }
.tc-hint { font-size: 0.8rem; color: var(--text-secondary); margin: 8px 0 0; }
</style>
